import { create } from 'zustand';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { errorMessage } from '../i18n/errorMessages';
import * as api from '../lib/api';
import {
  createIdempotencyKey,
  normalizeServerInput,
  validateServerInput,
} from '../lib/createServer';
import type { Guild, GuildMember } from '../lib/api';

export type ServerMember = GuildMember;
export type ServerMemberLoadResult = { status: 'applied' | 'superseded' };
export type MembershipReconciliationResult = { status: 'applied' | 'superseded' };

export interface CreateServerIntent {
  kind: 'intent';
  name: string;
  icon: string;
  idempotencyKey: string;
}

export interface CreateServerReceipt {
  kind: 'receipt';
  key: string;
  guildId: string;
  landingChannelId: string;
}

type CreateServerRecovery = CreateServerIntent | CreateServerReceipt;
export type GuildRefreshResult =
  { status: 'applied'; guilds: Guild[] } | { status: 'superseded'; guilds: Guild[] };

interface ServerState {
  guilds: Guild[];
  guildsLoaded: boolean;
  members: ServerMember[];
  loaded: boolean;
  loadedGuildId: string | null;
  accountId: string | null;
  authorityRevision: number;
  guildsAuthorityRevision: number | null;
  membersAuthorityRevision: number | null;
  membershipRefreshing: boolean;
  membershipError: MessageDescriptor | null;
  createRecovery: CreateServerRecovery | null;
  createTerminalGuildId: string | null;
  createBusy: boolean;
  createError: MessageDescriptor[] | null;

  bindAccount: (accountId: string) => void;
  refreshGuilds: (forceFresh?: boolean) => Promise<GuildRefreshResult>;
  createServer: (name?: string, icon?: string) => Promise<CreateServerReceipt>;
  clearCreateReceipt: () => void;
  setCreateError: (message: MessageDescriptor | null) => void;
  clearMembers: () => void;
  replaceMembers: (guildId: string, members: ServerMember[]) => void;
  replaceGuild: (guild: Guild) => void;
  loadMembers: (guildId: string) => Promise<ServerMemberLoadResult>;
  invalidateMembershipAuthority: () => void;
  reconcileMembership: (guildId: string | null) => Promise<MembershipReconciliationResult>;
  removeMember: (userId: string) => void;
  reset: () => void;
}

const RECOVERY_PREFIX = 'borgee:create-server:';
let membersGeneration = 0;
let accountGeneration = 0;
let guildsGeneration = 0;
let guildsInFlight: { generation: number; promise: Promise<GuildRefreshResult> } | null = null;
let membershipReconciliation: {
  accountId: string;
  guildId: string | null;
  promise: Promise<MembershipReconciliationResult>;
} | null = null;

function recoveryKey(accountId: string): string {
  return RECOVERY_PREFIX + accountId;
}

function loadRecovery(accountId: string): CreateServerRecovery | null {
  try {
    const raw = localStorage.getItem(recoveryKey(accountId));
    return raw ? (JSON.parse(raw) as CreateServerRecovery) : null;
  } catch {
    return null;
  }
}

function persistRecovery(accountId: string, recovery: CreateServerRecovery | null): void {
  try {
    if (recovery) localStorage.setItem(recoveryKey(accountId), JSON.stringify(recovery));
    else localStorage.removeItem(recoveryKey(accountId));
  } catch {
    throw new Error(
      recovery
        ? 'Could not save Project creation recovery. Check browser storage and try again.'
        : 'Could not clear Project creation recovery. Check browser storage and try again.',
    );
  }
}

export const useServerStore = create<ServerState>()((set, get) => ({
  guilds: [],
  guildsLoaded: false,
  members: [],
  loaded: false,
  loadedGuildId: null,
  accountId: null,
  authorityRevision: 0,
  guildsAuthorityRevision: null,
  membersAuthorityRevision: null,
  membershipRefreshing: false,
  membershipError: null,
  createRecovery: null,
  createTerminalGuildId: null,
  createBusy: false,
  createError: null,

  bindAccount: (accountId) => {
    if (get().accountId === accountId) return;
    accountGeneration++;
    guildsGeneration++;
    membersGeneration++;
    guildsInFlight = null;
    membershipReconciliation = null;
    set({
      accountId,
      guilds: [],
      guildsLoaded: false,
      members: [],
      loaded: false,
      loadedGuildId: null,
      authorityRevision: 0,
      guildsAuthorityRevision: null,
      membersAuthorityRevision: null,
      membershipRefreshing: false,
      membershipError: null,
      createRecovery: loadRecovery(accountId),
      createTerminalGuildId: null,
      createBusy: false,
      createError: null,
    });
  },

  refreshGuilds: (forceFresh = false) => {
    const accountId = get().accountId;
    if (!accountId) return Promise.resolve({ status: 'superseded', guilds: [] });
    const authorityRevision = get().authorityRevision;
    if (forceFresh) {
      // A refresh started before a successful create response cannot prove the
      // new membership exists. Supersede it and issue a post-commit read rather
      // than coalescing creation reconciliation onto a stale snapshot.
      guildsGeneration++;
      guildsInFlight = null;
    }
    const generation = guildsGeneration;
    if (guildsInFlight?.generation === generation) return guildsInFlight.promise;
    const promise = api
      .fetchGuilds()
      .then((guilds): GuildRefreshResult => {
        if (generation !== guildsGeneration || get().accountId !== accountId) {
          return { status: 'superseded', guilds };
        }
        const recovery = get().createRecovery;
        if (
          recovery?.kind === 'receipt' &&
          !guilds.some((guild) => guild.id === recovery.guildId)
        ) {
          // The membership response is authoritative even when browser storage
          // is unavailable. Never retain a revoked Server in live state merely
          // because its stale recovery key could not be removed.
          const messages = [msg`You no longer have access to the created Project.`];
          try {
            persistRecovery(accountId, null);
          } catch {
            messages.push(msg`Browser storage could not be cleared; reload may retry recovery.`);
          }
          set({
            guilds,
            guildsLoaded: true,
            guildsAuthorityRevision: authorityRevision,
            createRecovery: null,
            createTerminalGuildId: recovery.guildId,
            createError: messages,
          });
        } else {
          set({ guilds, guildsLoaded: true, guildsAuthorityRevision: authorityRevision });
        }
        const state = get();
        const activeGuildId = state.loadedGuildId;
        if (
          forceFresh &&
          activeGuildId &&
          state.membersAuthorityRevision !== state.authorityRevision
        ) {
          membershipReconciliation = null;
          void state.reconcileMembership(activeGuildId).catch(() => undefined);
        }
        return { status: 'applied', guilds };
      })
      .finally(() => {
        if (guildsInFlight?.promise === promise) guildsInFlight = null;
      });
    guildsInFlight = { generation, promise };
    return promise;
  },

  createServer: async (name, icon) => {
    const accountId = get().accountId;
    if (!accountId) throw new Error('Sign in before creating a Project.');
    if (get().createBusy) throw new Error('Project creation is already in progress.');
    const generation = accountGeneration;
    const ownsResult = () => generation === accountGeneration && get().accountId === accountId;

    let recovery = get().createRecovery;
    if (!recovery) {
      const normalized = normalizeServerInput(name ?? '', icon ?? '');
      const errors = validateServerInput(normalized);
      if (errors.name || errors.icon) {
        set({ createError: [errors.name ?? errors.icon!] });
        throw new Error('Project creation input is invalid.');
      }
      recovery = {
        kind: 'intent',
        ...normalized,
        idempotencyKey: createIdempotencyKey(),
      };
      try {
        persistRecovery(accountId, recovery);
      } catch (error) {
        set({
          createError: [
            msg`Could not save Project creation recovery. Check browser storage and try again.`,
          ],
        });
        throw error;
      }
      set({ createRecovery: recovery, createTerminalGuildId: null });
    }

    set({ createBusy: true, createError: null });
    try {
      if (recovery.kind === 'intent') {
        const result = await api.createGuild(
          { name: recovery.name, icon: recovery.icon },
          recovery.idempotencyKey,
        );
        if (!ownsResult()) throw new Error('Project creation was superseded by an account change.');
        recovery = {
          kind: 'receipt',
          key: recovery.idempotencyKey,
          guildId: result.guild.id,
          landingChannelId: result.landing_channel_id,
        };
        persistRecovery(accountId, recovery);
        set({ createRecovery: recovery });
      }

      const receipt = recovery as CreateServerReceipt;
      const refreshed = await get().refreshGuilds(true);
      if (!ownsResult() || refreshed.status !== 'applied')
        throw new Error('The Project list refresh was superseded.');
      if (!refreshed.guilds.some((guild) => guild.id === receipt.guildId)) {
        // refreshGuilds already reconciles a missing receipt membership and
        // clears live recovery best-effort. Do not let a second storage removal
        // mask the authoritative access error.
        const terminalMessage = get().createError ?? [
          msg`You no longer have access to the created Project.`,
        ];
        set({
          createRecovery: null,
          createTerminalGuildId: receipt.guildId,
          createError: terminalMessage,
        });
        throw new Error('You no longer have access to the created Project.');
      }
      return receipt;
    } catch (error) {
      if (!ownsResult()) throw error;
      const context =
        recovery.kind === 'receipt'
          ? msg`The Project was created, but recovery could not be completed. Retry to load the Project.`
          : msg`Could not create the Project.`;
      const detail = errorMessage(error, context);
      const messages = get().createTerminalGuildId
        ? (get().createError ?? [msg`You no longer have access to the created Project.`])
        : detail === context
          ? [context]
          : [context, detail];
      const deterministicFailure =
        error instanceof api.ApiError && [400, 401, 403, 409].includes(error.status);
      if (deterministicFailure && recovery.kind === 'intent') {
        // A terminal server response unlocks editing in this session even if a
        // stale durable key cannot be removed. On reload the same idempotency
        // key can only replay the same terminal response; it must not keep the
        // modal permanently locked now.
        try {
          persistRecovery(accountId, null);
        } catch {
          messages.push(msg`Browser storage could not be cleared; reload may retry this request.`);
        }
        set({ createRecovery: null, createError: messages });
      } else {
        set({ createError: messages });
      }
      throw error;
    } finally {
      if (ownsResult()) set({ createBusy: false });
    }
  },

  clearCreateReceipt: () => {
    const { accountId, createRecovery } = get();
    if (!accountId || createRecovery?.kind !== 'receipt') return;
    let createError: MessageDescriptor[] | null = null;
    try {
      persistRecovery(accountId, null);
    } catch {
      // The Server context is already settled. Clear the in-memory receipt so
      // the UI cannot remain locked forever; a stale durable receipt is safe to
      // replay because the create command is idempotent.
      createError = [msg`Browser storage could not be cleared; reload may retry Project recovery.`];
    }
    set({ createRecovery: null, createError });
  },

  setCreateError: (message) => set({ createError: message ? [message] : null }),

  clearMembers: () => {
    membersGeneration++;
    set({
      members: [],
      loaded: false,
      loadedGuildId: null,
      membersAuthorityRevision: null,
      membershipError: null,
    });
  },

  replaceMembers: (guildId, members) => {
    membersGeneration++;
    set({
      members,
      loaded: true,
      loadedGuildId: guildId,
      membersAuthorityRevision: get().authorityRevision,
      membershipError: null,
    });
  },

  // Server profile changes are reflected immediately in every consumer of the
  // shared Server list: the selected-Server rail, header, and switcher.
  replaceGuild: (guild) =>
    set((state) => ({
      guilds: state.guilds.map((current) => (current.id === guild.id ? guild : current)),
    })),

  loadMembers: async (guildId) => {
    const generation = ++membersGeneration;
    const accountId = get().accountId;
    const retainRoster = get().loadedGuildId === guildId;
    set({
      ...(retainRoster ? {} : { members: [] }),
      loaded: false,
      loadedGuildId: guildId,
      membersAuthorityRevision: null,
      membershipError: null,
    });
    try {
      const members = await api.fetchGuildMembers(guildId);
      if (generation !== membersGeneration || get().accountId !== accountId)
        return { status: 'superseded' };
      set((state) => ({
        members,
        loaded: true,
        loadedGuildId: guildId,
        membersAuthorityRevision: state.authorityRevision,
        membershipError: null,
      }));
      return { status: 'applied' };
    } catch (error) {
      if (generation === membersGeneration && get().accountId === accountId) {
        set({
          loaded: false,
          loadedGuildId: guildId,
          membershipError: errorMessage(error, msg`Could not refresh Project membership.`),
        });
      }
      throw error;
    }
  },

  invalidateMembershipAuthority: () => {
    membersGeneration++;
    membershipReconciliation = null;
    set((state) => ({
      authorityRevision: state.authorityRevision + 1,
      membershipRefreshing: false,
      membershipError: null,
    }));
  },

  reconcileMembership: (guildId) => {
    const accountId = get().accountId;
    if (!accountId) return Promise.resolve({ status: 'superseded' });

    const state = get();
    const authorityRevision = state.authorityRevision + 1;
    const targetsLoadedRoster = guildId !== null && state.loadedGuildId === guildId;
    if (targetsLoadedRoster) membersGeneration++;
    set({
      authorityRevision,
      membershipRefreshing: true,
      ...(targetsLoadedRoster
        ? { membershipError: null }
        : state.loaded && state.membersAuthorityRevision === state.authorityRevision
          ? { membersAuthorityRevision: authorityRevision }
          : {}),
    });

    if (
      membershipReconciliation?.accountId === accountId &&
      membershipReconciliation.guildId === guildId
    ) {
      return membershipReconciliation.promise;
    }

    // Retire only Guild-list reads that began before this invalidation. A
    // post-commit refresh started afterwards remains valid and must not be
    // superseded when this reconciliation eventually publishes its result.
    const guildsFence = ++guildsGeneration;
    guildsInFlight = null;
    const job = {
      accountId,
      guildId,
      promise: Promise.resolve({ status: 'superseded' } as MembershipReconciliationResult),
    };
    job.promise = (async (): Promise<MembershipReconciliationResult> => {
      for (;;) {
        const revision = get().authorityRevision;
        let membersFence: number | null = null;
        try {
          const [viewer, guilds] = await Promise.all([api.fetchMe(), api.fetchGuilds()]);
          if (viewer.id !== accountId) {
            throw new Error('Project membership refresh belongs to another account.');
          }
          if (
            membershipReconciliation !== job ||
            get().accountId !== accountId ||
            guildsGeneration !== guildsFence
          )
            return { status: 'superseded' };
          if (get().authorityRevision !== revision) continue;

          if (!guildId || !guilds.some((guild) => guild.id === guildId)) {
            set({
              guilds,
              guildsLoaded: true,
              guildsAuthorityRevision: revision,
              ...(guildId && get().loadedGuildId === guildId
                ? { membersAuthorityRevision: null }
                : {}),
            });
            return { status: 'applied' };
          }

          if (get().loadedGuildId !== guildId) {
            set({
              guilds,
              guildsLoaded: true,
              guildsAuthorityRevision: revision,
            });
            return { status: 'applied' };
          }

          // Claim the current Project roster only after the Guild projection
          // reaches this authority revision. This retires an older direct load
          // that cannot publish at the new revision, while a load started after
          // this point can still take ownership of the roster.
          membersFence = ++membersGeneration;

          let members: ServerMember[];
          let confirmedViewer: Awaited<ReturnType<typeof api.fetchMe>>;
          try {
            [members, confirmedViewer] = await Promise.all([
              api.fetchGuildMembers(guildId),
              api.fetchMe(),
            ]);
          } catch (error) {
            if (
              membershipReconciliation === job &&
              get().accountId === accountId &&
              guildsGeneration === guildsFence &&
              get().authorityRevision === revision
            ) {
              set({ guilds, guildsLoaded: true, guildsAuthorityRevision: revision });
            }
            throw error;
          }
          if (confirmedViewer.id !== accountId) {
            throw new Error('Project membership refresh belongs to another account.');
          }
          if (
            membershipReconciliation !== job ||
            get().accountId !== accountId ||
            guildsGeneration !== guildsFence
          )
            return { status: 'superseded' };
          if (get().authorityRevision !== revision) continue;

          const rosterStillCurrent =
            membersGeneration === membersFence && get().loadedGuildId === guildId;
          set({
            guilds,
            guildsLoaded: true,
            guildsAuthorityRevision: revision,
            ...(rosterStillCurrent
              ? {
                  members,
                  loaded: true,
                  loadedGuildId: guildId,
                  membersAuthorityRevision: revision,
                  membershipError: null,
                }
              : {}),
          });
          return { status: 'applied' };
        } catch (error) {
          if (
            membershipReconciliation !== job ||
            get().accountId !== accountId ||
            guildsGeneration !== guildsFence
          )
            return { status: 'superseded' };
          if (get().authorityRevision !== revision) continue;
          if (
            membersFence === null ||
            (membersGeneration === membersFence && get().loadedGuildId === guildId)
          ) {
            set({
              membershipError: errorMessage(error, msg`Could not refresh Project membership.`),
            });
          }
          throw error;
        }
      }
    })().finally(() => {
      if (membershipReconciliation === job) {
        membershipReconciliation = null;
        if (get().accountId === accountId) set({ membershipRefreshing: false });
      }
    });
    membershipReconciliation = job;
    return job.promise;
  },

  removeMember: (userId) =>
    set((state) => ({
      members: state.members.filter((member) => member.user_id !== userId),
    })),

  reset: () => {
    const accountId = get().accountId;
    if (accountId) {
      try {
        persistRecovery(accountId, null);
      } catch {
        // Logout/reset must still clear in-memory account state when browser
        // storage is unavailable; the key is account-scoped and cannot leak to
        // a different account.
      }
    }
    membersGeneration++;
    accountGeneration++;
    guildsGeneration++;
    guildsInFlight = null;
    membershipReconciliation = null;
    set({
      guilds: [],
      guildsLoaded: false,
      members: [],
      loaded: false,
      loadedGuildId: null,
      accountId: null,
      authorityRevision: 0,
      guildsAuthorityRevision: null,
      membersAuthorityRevision: null,
      membershipRefreshing: false,
      membershipError: null,
      createRecovery: null,
      createTerminalGuildId: null,
      createBusy: false,
      createError: null,
    });
  },
}));
